/**
 * Marks a phone has taken but not yet delivered.
 *
 * Two things this survives. A fellowship hall with three hundred phones on one
 * access point, where the tap happens and the request does not; and a teacher
 * who is not signed in yet, whose mark waits on this device while she signs in
 * and goes out the moment she is back.
 *
 * The queue holds codes, which name sessions and never people. Nothing here is
 * worth protecting if the phone is lost, and nothing here means anything on a
 * different phone.
 */
const STORAGE_KEY = "wvcea-presence-pending-v1";

export function pendingPresenceCodes(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
  } catch {
    return [];
  }
}

export function rememberPresenceCode(code: string): void {
  const next = [...new Set([...pendingPresenceCodes(), code])];
  write(next);
}

export function forgetPresenceCode(code: string): void {
  write(pendingPresenceCodes().filter((entry) => entry !== code));
}

/**
 * Send everything waiting, and keep whatever could not go.
 *
 * A refusal the server actually made — an unknown code, a registration that is
 * not hers — drops the code rather than keeping it forever: retrying it on every
 * app start would never succeed and would hide the ones that can. A failure to
 * reach the server at all keeps it.
 */
export async function flushPendingPresence(eventSlug: string): Promise<number> {
  let sent = 0;
  for (const code of pendingPresenceCodes()) {
    try {
      const response = await fetch(
        `/api/attendee/presence/${encodeURIComponent(code)}/mark`,
        { method: "POST", credentials: "same-origin" },
      );
      if (response.ok) {
        forgetPresenceCode(code);
        sent += 1;
        continue;
      }
      // 401 and 403 mean she is not signed in yet, or this account has no
      // registration at this event. Both are worth waiting for.
      if (response.status !== 401 && response.status !== 403) forgetPresenceCode(code);
    } catch {
      // The network. Keep it and try again next time the app opens.
      return sent;
    }
  }
  void eventSlug;
  return sent;
}

function write(codes: readonly string[]): void {
  try {
    if (codes.length === 0) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
  } catch {
    // A browser with storage turned off still marks attendance; it just cannot
    // hold a mark across a lost connection. Losing the queue is not worth an error.
  }
}
